import { col, weekGridClasses } from '@utils/styles'
import classNames from 'classnames'
import { WeeklyProgressGauge } from 'components/indicators/weekly-progress-gauge'
import { Spinner } from 'components/loaders'
import { FC } from 'react'
import { WorkedAndAllocated } from './WorkedAndAllocated'
import { useAbsences } from '@hooks/useAbsences'
import { Day } from './Day'
import { useTotalsForWeek } from '@features/time-logging/hooks/useTotalsForWeek'
import { DayAnnotation } from './day-annotation'
import { useWeekScope } from '@hooks/useWeekScope'
import { useHolidays } from '@features/time-logging/hooks/useHolidays'
import { useUserId } from '@features/time-logging/hooks/useUserId'
import { useTimeCardsContext } from '@features/time-logging/providers/TimeCardsContextProvider'

interface Props {
  onTimeOffClick: () => void
  readOnly: boolean
}

export const TimeCards: FC<Props> = ({ onTimeOffClick, readOnly }) => {
  const { end, range, start, timeCards, isLoading, totalHours } =
    useTimeCardsContext()
  const holidaysQuery = useHolidays({ start, end })

  const userId = useUserId()
  const { weekStartsOn } = useWeekScope()
  const { data: absences = [] } = useAbsences({ userId, start, end })
  const { absenceSeconds, holidaySeconds, allocatedSeconds } = useTotalsForWeek(
    { start, end },
  )

  return (
    <div className="px-8 py-5 border-b bg-neutral-50 border-neutral-200">
      <div className={`mb-[-2px] ${weekGridClasses(weekStartsOn)}`}>
        {range.map((date) => (
          <DayAnnotation
            key={`annotations-${date.toString()}`}
            date={date}
            absences={absences}
            holidays={holidaysQuery?.data ?? []}
            onTimeOffClick={onTimeOffClick}
            weekStartDate={start}
            weekEndDate={end}
          />
        ))}
      </div>
      <div className={weekGridClasses(weekStartsOn)}>
        <WorkedAndAllocated />
        {isLoading
          ? range.map((date) => (
              <div
                key={date.toString()}
                className={classNames(
                  'flex items-center justify-center',
                  col(date),
                )}
              >
                <Spinner />
              </div>
            ))
          : timeCards.map((card) => (
              <div
                className={`flex px-1 ${col(card.date)}`}
                key={`time-card-slot-${card.date.toISO()}`}
              >
                <Day timeCard={card} readOnly={readOnly} />
              </div>
            ))}
        <div className="flex flex-col justify-end grid-in-totalByTask">
          <WeeklyProgressGauge
            numAbsenceSeconds={absenceSeconds}
            numHolidaySeconds={holidaySeconds}
            numWorkSeconds={allocatedSeconds}
            totalHours={totalHours}
            loading={isLoading}
            className="grow"
          />
        </div>
      </div>
    </div>
  )
}
